import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined } from '@ant-design/icons';
import { convertTitleToUrl } from '../../shared/utils/url';

export type NextMatch = { when: string; opponent: string };

// "This season" at a glance: rank, points, record, win rate and the next booked match
// (docs/UI-UX-SYSTEM.md §16.3-E).
export default function LeagueSummary({
  user,
  league,
  nextMatch,
}: {
  user: Users.User | null;
  league: Leagues.League;
  nextMatch: NextMatch | null;
}) {
  const { t } = useTranslation();
  const kazanacakPuan = user?.piramit_etken_puan !== 0 ? user?.piramit_etken_puan : 10;
  // Eksik sayaç bilinmeyen değerdir; 0 gibi gösterilmez (UI-UX-SYSTEM: eksik sayı `—`).
  const hasRecord = typeof user?.galibiyet === 'number' || typeof user?.maglubiyet === 'number';
  const wins = user?.galibiyet || 0;
  const losses = user?.maglubiyet || 0;
  const played = wins + losses;
  const winRate = played ? Math.round((wins / played) * 100) : null;

  return (
    <section className="league-summary" aria-labelledby="league-summary-title">
      <div className="league-summary-head">
        <span className="league-summary-eyebrow">{t('profile.leagueSummary.thisSeason')}</span>
        <Link href={convertTitleToUrl('ligler', league.lig_ad, league.id)}>
          <a className="league-summary-title" id="league-summary-title">
            {league.lig_ad}
          </a>
        </Link>
      </div>

      <dl className="league-summary-stats">
        <div className="league-summary-stat">
          <dt>{t('profile.leagueSummary.rank')}</dt>
          <dd>{user?.piramit_sira ? `#${user.piramit_sira}` : '—'}</dd>
        </div>
        <div className="league-summary-stat league-summary-stat--accent">
          <dt>{t('profile.leagueSummary.totalPoints')}</dt>
          <dd>{user?.piramit_puan ?? '—'}</dd>
        </div>
        <div className="league-summary-stat">
          <dt>{t('profile.leagueSummary.record')}</dt>
          {hasRecord ? (
            <dd>
              {wins}
              <span className="league-summary-sep" aria-hidden="true">
                –
              </span>
              <span className="visually-hidden"> / </span>
              {losses}
            </dd>
          ) : (
            <dd>—</dd>
          )}
        </div>
        <div className="league-summary-stat">
          <dt>{t('profile.leagueSummary.winRate')}</dt>
          <dd>{winRate === null ? '—' : t('profile.leagueSummary.winRateValue', { value: winRate })}</dd>
        </div>
      </dl>

      <div className="league-summary-gain">
        <span>{t('profile.leagueSummary.winPoints')}</span>
        <strong>+{kazanacakPuan}</strong>
      </div>

      {nextMatch && (
        <p className="league-summary-next">
          <CalendarOutlined aria-hidden="true" />
          <span>
            <b>{t('profile.leagueSummary.nextMatch')}</b> {nextMatch.when} ·{' '}
            {t('profile.leagueSummary.versus', { name: nextMatch.opponent })}
          </span>
        </p>
      )}
    </section>
  );
}
